import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  useAccount,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import { addresses, stakingAbi, erc20Abi } from "../config/contracts";
import { formatFanz, parseFanz } from "../lib/formatBigInt";
import { SectionHeader } from "./Tokenomics";
import { AnimatedNumber } from "./AnimatedNumber";

const HAS_STAKING = addresses.staking !== "0x0000000000000000000000000000000000000000";
const HAS_TOKEN = addresses.token !== "0x0000000000000000000000000000000000000000";
const APY_BPS = 2000n; // 20% — matches FanzStaking.sol default
const COOLDOWN_SECONDS = 7n * 86400n;

export default function Staking() {
  return (
    <section id="staking" className="relative mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28">
      <SectionHeader
        eyebrow="Staking"
        title="Stake $FANZ. Earn 20% APY."
        sub="One pool. Continuous accrual. Seven-day cooldown on withdraw — predictable
             unlocks, no surprise lockups, no slashing. Stake from a wallet or auto-stake
             on presale claim."
      />

      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[1.05fr_1fr]">
        <APYShowcase />
        <StakeWidget />
      </div>

      <CooldownInfo />
    </section>
  );
}

function APYShowcase() {
  const { data: totalStaked } = useReadContract({
    address: addresses.staking,
    abi: stakingAbi,
    functionName: "totalStaked",
    query: { enabled: HAS_STAKING, refetchInterval: 60_000 },
  });
  const { data: apyBps } = useReadContract({
    address: addresses.staking,
    abi: stakingAbi,
    functionName: "apyBps",
    query: { enabled: HAS_STAKING, refetchInterval: 300_000 },
  });

  const apy = Number((apyBps ?? APY_BPS) ?? 0n) / 100;
  const totalNum = Number((totalStaked ?? 0n) / 10n ** 18n);

  return (
    <div className="panel relative overflow-hidden p-6 md:p-8">
      <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-accent/30 blur-3xl" />
      <div className="absolute -bottom-16 -left-10 h-44 w-44 rounded-full bg-magenta/20 blur-3xl" />

      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-300">
          Live yield
        </div>

        <div className="mt-2 flex items-baseline gap-3">
          <span className="font-display text-[clamp(5rem,16vw,9rem)] leading-none text-iridescent">
            {apy.toFixed(0)}%
          </span>
          <span className="font-display text-3xl text-white/80">APY</span>
        </div>

        <p className="mt-3 max-w-md text-base text-gray-300">
          Fixed-rate, continuously accruing. Rewards stream from the dedicated 5%
          staking allocation pre-deposited into the staking contract — no inflation,
          no rebase tricks.
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Tile
            label="Pool TVL"
            value={<AnimatedNumber value={totalNum} />}
            suffix="FANZ"
          />
          <Tile label="Cooldown" value="7 days" />
          <Tile label="Pool" value="Single" />
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, suffix }: { label: string; value: React.ReactNode; suffix?: string }) {
  return (
    <div className="rounded-xl border border-white/[0.07] bg-black/30 p-3">
      <div className="text-[9px] uppercase tracking-widest text-gray-300">{label}</div>
      <div className="font-mono text-base text-white">
        {value} {suffix && <span className="text-xs text-gray-300">{suffix}</span>}
      </div>
    </div>
  );
}

function StakeWidget() {
  const { address, isConnected } = useAccount();
  const [amount, setAmount] = useState("100");
  const [mode, setMode] = useState<"stake" | "unstake" | "claim">("stake");

  const { data: balance } = useReadContract({
    address: addresses.token,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
    query: { enabled: !!address && HAS_TOKEN, refetchInterval: 30_000 },
  });
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: addresses.token,
    abi: erc20Abi,
    functionName: "allowance",
    args: address ? [address, addresses.staking] : undefined,
    query: { enabled: !!address && HAS_TOKEN && HAS_STAKING, refetchInterval: 30_000 },
  });
  const { data: stakeTuple } = useReadContract({
    address: addresses.staking,
    abi: stakingAbi,
    functionName: "stakes",
    args: address ? [address] : undefined,
    query: { enabled: !!address && HAS_STAKING, refetchInterval: 12_000 },
  });
  const { data: pendingReward } = useReadContract({
    address: addresses.staking,
    abi: stakingAbi,
    functionName: "pendingReward",
    args: address ? [address] : undefined,
    query: { enabled: !!address && HAS_STAKING, refetchInterval: 12_000 },
  });

  const stakedAmount = stakeTuple?.[0] ?? 0n;
  const pendingWithdraw = stakeTuple?.[3] ?? 0n;
  const unlocksAt = stakeTuple?.[4] ?? 0n;
  const balanceNum = Number((balance ?? 0n) / 10n ** 18n);
  const stakedNum = Number(stakedAmount / 10n ** 18n);
  const pendingNum = Number((pendingReward ?? 0n) / 10n ** 18n);
  const pendingWdNum = Number(pendingWithdraw / 10n ** 18n);

  const amountWei = useMemo(() => {
    try {
      if (!amount) return 0n;
      return parseFanz(amount);
    } catch {
      return 0n;
    }
  }, [amount]);

  const needsApproval = useMemo(() => {
    if (mode !== "stake" || !HAS_STAKING || !HAS_TOKEN) return false;
    return (allowance ?? 0n) < amountWei;
  }, [allowance, amountWei, mode]);

  const { writeContract, data: hash, isPending, error } = useWriteContract();
  const { isLoading: txLoading, isSuccess: txDone } = useWaitForTransactionReceipt({ hash });

  useEffect(() => {
    if (txDone) refetchAllowance();
  }, [txDone, refetchAllowance]);

  const onApprove = () =>
    writeContract({
      address: addresses.token,
      abi: erc20Abi,
      functionName: "approve",
      args: [addresses.staking, amountWei],
    });

  const onSubmit = () => {
    if (!HAS_STAKING) return;
    if (mode === "stake")
      writeContract({
        address: addresses.staking,
        abi: stakingAbi,
        functionName: "stake",
        args: [amountWei],
      });
    else if (mode === "unstake")
      writeContract({
        address: addresses.staking,
        abi: stakingAbi,
        functionName: "requestWithdraw",
        args: [amountWei],
      });
    else if (mode === "claim")
      writeContract({
        address: addresses.staking,
        abi: stakingAbi,
        functionName: "claim",
        args: [],
      });
  };

  const ctaLabel = !isConnected
    ? "Connect wallet"
    : !HAS_STAKING
      ? "Pre-Deploy"
      : mode === "claim"
        ? "Claim rewards"
        : needsApproval
          ? "Approve FANZ"
          : mode === "stake"
            ? "Stake FANZ"
            : "Request unstake";

  const ctaAction = !HAS_STAKING || !isConnected ? () => {} : needsApproval ? onApprove : onSubmit;

  return (
    <div className="panel panel-glow p-6 md:p-8">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-300">
            Stake panel
          </div>
          <h3 className="mt-1 font-display text-3xl text-white">Earn yield</h3>
        </div>
        {isConnected && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-gray-300">Pending reward</div>
            <div className="font-mono text-base text-accent">
              <AnimatedNumber value={pendingNum} decimals={4} /> FANZ
            </div>
          </div>
        )}
      </div>

      <div className="mb-4 grid grid-cols-3 gap-2">
        {(["stake", "unstake", "claim"] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`relative overflow-hidden rounded-xl border px-3 py-2.5 text-sm font-semibold capitalize transition-all ${
              mode === m
                ? "border-accent bg-accent/[0.08] text-white"
                : "border-white/[0.07] bg-white/[0.02] text-gray-300 hover:border-white/20"
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      {mode !== "claim" && (
        <div className="rounded-2xl border border-white/[0.07] bg-black/40 p-4">
          <div className="flex items-center justify-between text-xs text-gray-300">
            <span>{mode === "stake" ? "Amount to stake" : "Amount to unstake"}</span>
            <button
              onClick={() =>
                setAmount(
                  formatFanz(mode === "stake" ? balance ?? 0n : stakedAmount, 0).replace(/,/g, ""),
                )
              }
              className="text-xs uppercase tracking-widest text-accent hover:underline"
            >
              MAX
            </button>
          </div>
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
            inputMode="decimal"
            placeholder="100"
            className="mt-1 w-full bg-transparent font-mono text-3xl font-semibold text-white placeholder:text-white/20 focus:outline-none"
          />
          <div className="mt-1 flex items-center justify-between text-[11px] text-gray-300">
            <span>
              Wallet: <span className="font-mono text-white/80">{balanceNum.toLocaleString()}</span> FANZ
            </span>
            <span>
              Staked: <span className="font-mono text-white/80">{stakedNum.toLocaleString()}</span> FANZ
            </span>
          </div>
        </div>
      )}

      {mode === "claim" && (
        <div className="rounded-2xl border border-accent/20 bg-gradient-to-br from-accent/[0.08] to-transparent p-5 text-center">
          <div className="text-[10px] uppercase tracking-widest text-gray-300">Available to claim</div>
          <div className="mt-2 font-mono text-4xl font-semibold text-white">
            <AnimatedNumber value={pendingNum} decimals={4} />
          </div>
          <div className="mt-1 text-sm text-accent">FANZ</div>
        </div>
      )}

      <button
        onClick={ctaAction}
        disabled={!isConnected || !HAS_STAKING || isPending || txLoading || (mode !== "claim" && amountWei === 0n)}
        className="btn-primary mt-5 w-full"
      >
        {isPending || txLoading ? "Confirming…" : ctaLabel}
      </button>

      {pendingWithdraw > 0n && (
        <CooldownTimer unlocksAt={unlocksAt} pendingWdNum={pendingWdNum} />
      )}

      {error && (
        <div className="mt-3 rounded-xl border border-error/30 bg-error/[0.05] p-3 text-sm text-error/90">
          {error.message.split("\n")[0]}
        </div>
      )}
    </div>
  );
}

function CooldownTimer({ unlocksAt, pendingWdNum }: { unlocksAt: bigint; pendingWdNum: number }) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const target = Number(unlocksAt);
  const remaining = Math.max(0, target - now);
  const total = Number(COOLDOWN_SECONDS);
  const elapsed = total - remaining;
  const pct = total > 0 ? Math.min(100, (elapsed / total) * 100) : 0;
  const ready = remaining === 0;

  const { writeContract, data: hash, isPending } = useWriteContract();
  const { isLoading } = useWaitForTransactionReceipt({ hash });

  const days = Math.floor(remaining / 86400);
  const hours = Math.floor((remaining % 86400) / 3600);
  const mins = Math.floor((remaining % 3600) / 60);

  return (
    <div className="mt-4 rounded-2xl border border-white/[0.07] bg-black/30 p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-300">
            Cooldown active
          </div>
          <div className="mt-1 font-mono text-sm text-white">
            {pendingWdNum.toLocaleString()} FANZ
          </div>
        </div>
        <CooldownDial pct={pct} />
      </div>
      <div className="mt-2 text-center font-mono text-base text-white">
        {ready ? (
          <span className="text-success">Ready to withdraw</span>
        ) : (
          <span className="tabular-nums">
            {days}d {hours}h {mins}m
          </span>
        )}
      </div>
      {ready && (
        <button
          disabled={isPending || isLoading}
          onClick={() =>
            writeContract({
              address: addresses.staking,
              abi: stakingAbi,
              functionName: "withdraw",
              args: [],
            })
          }
          className="btn-primary mt-3 w-full text-sm"
        >
          {isPending || isLoading ? "Confirming…" : "Withdraw"}
        </button>
      )}
    </div>
  );
}

function CooldownDial({ pct }: { pct: number }) {
  const safe = Math.max(0, Math.min(100, pct));
  const r = 22;
  const c = 2 * Math.PI * r;
  const offset = c - (c * safe) / 100;
  return (
    <svg width={56} height={56} viewBox="0 0 56 56" className="rotate-[-90deg]">
      <circle cx={28} cy={28} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth={4} fill="none" />
      <circle
        cx={28}
        cy={28}
        r={r}
        stroke="url(#dialGrad)"
        strokeWidth={4}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        style={{ transition: "stroke-dashoffset 1s linear" }}
      />
      <defs>
        <linearGradient id="dialGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#c084fc" />
          <stop offset="100%" stopColor="#ff2d92" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function CooldownInfo() {
  const items = [
    {
      title: "Continuous accrual",
      body: "Rewards stream every block. Claim any time without unstaking — your principal keeps earning.",
    },
    {
      title: "7-day cooldown",
      body: "When you request a withdraw, FANZ is queued for 7 days. No early-exit penalty, no surprise lockups.",
    },
    {
      title: "Auto-stake on claim",
      body: "When you claim presale tokens you can route them straight into the staking pool in a single tx — gas-efficient compounding.",
    },
  ];
  return (
    <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
      {items.map((it, i) => (
        <motion.div
          key={it.title}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.08, duration: 0.5 }}
          className="panel panel-hover p-5"
        >
          <div className="mb-2 grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-accent to-accent-deep">
            <span className="font-mono text-sm font-bold text-white">0{i + 1}</span>
          </div>
          <div className="font-display text-xl text-white">{it.title}</div>
          <p className="mt-2 text-sm leading-relaxed text-gray-300">{it.body}</p>
        </motion.div>
      ))}
    </div>
  );
}
